import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Plus, Trash2 } from "lucide-react";

import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { MultiSelect, type OptionType } from "@/shared/components/ui/multi-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Switch } from "@/shared/components/ui/switch";
import { IntegrationService } from "@/features/Integrations/services/integrationsService";
import { textoDaSkill } from "@/features/Skills/utils/text";
import {
  CLASSIFICACAO,
  novaLinha,
  type ParametroEmEdicao,
  type RequisitoEmEdicao,
} from "../utils/skillDeclarations";

/**
 * As integrações que a skill exige.
 *
 * O requisito aponta um template (`jira`) ou uma família inteira
 * (`classification:project`) — a segunda forma é a recomendada, porque quem
 * escreve a skill não sabe qual ferramenta a empresa de destino usa.
 */
export const IntegrationsDeclarationStep = ({
  requisitos,
  onChange,
  idioma,
}: {
  requisitos: RequisitoEmEdicao[];
  onChange: (requisitos: RequisitoEmEdicao[]) => void;
  /** Em que idioma se escreve o que é lido por pessoas (AB#85634, AC37). */
  idioma: string;
}) => {
  const { t } = useTranslation();

  const { data: templates } = useQuery({
    queryKey: ["integration_templates"],
    queryFn: () => IntegrationService.getAll(),
  });

  /** Famílias primeiro: é a forma que sobrevive à empresa que usa outra ferramenta. */
  const opcoes: OptionType[] = useMemo(() => {
    const familias = [...new Set((templates ?? []).map((template) => template.classification))]
      .filter(Boolean)
      .map((familia) => ({
        value: `${CLASSIFICACAO}${familia}`,
        label: t("skills.publish.anyOfFamily", { family: familia }),
      }));

    const instancias = (templates ?? []).map((template) => ({
      value: template.name,
      label: template.label || template.name,
      logoUrl: template.logo_url ?? undefined,
    }));

    return [...familias, ...instancias];
  }, [templates, t]);

  const trocar = (indice: number, mudanca: Partial<RequisitoEmEdicao>) =>
    onChange(requisitos.map((item, i) => (i === indice ? { ...item, ...mudanca } : item)));

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{t("skills.publish.integrationsHint")}</p>

      {requisitos.length === 0 && (
        <p className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
          {t("skills.publish.integrationsEmpty")}
        </p>
      )}

      {requisitos.map((requisito, indice) => (
        // Chave pela identidade da LINHA, não pelo índice nem pelo texto: as
        // linhas são removíveis, e as duas outras opções fazem o React
        // reaproveitar o estado da linha errada depois de uma remoção.
        <div key={requisito.uid} className="space-y-3 rounded-md border p-4">
          <div className="flex items-start gap-3">
            <div className="flex-1 space-y-1.5">
              <Label>{t("skills.publish.requirementId")}</Label>
              <Input
                value={requisito.id}
                onChange={(evento) => trocar(indice, { id: evento.target.value })}
                placeholder="projeto"
              />
            </div>

            <Button
              variant="ghost"
              size="icon"
              className="mt-7 text-muted-foreground"
              onClick={() => onChange(requisitos.filter((_, i) => i !== indice))}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>

          <div className="space-y-1.5">
            <Label>{t("skills.publish.requirementAnyOf")}</Label>
            <MultiSelect
              options={opcoes}
              selected={requisito.anyOf}
              onChange={(escolhidas) => trocar(indice, { anyOf: escolhidas })}
              placeholder={t("skills.publish.requirementAnyOfPlaceholder")}
            />
            <p className="text-xs text-muted-foreground">
              {t("skills.publish.requirementAnyOfHint")}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label>{t("skills.publish.requirementReason")}</Label>
            <Input
              value={requisito.reason[idioma] ?? ""}
              onChange={(evento) =>
                trocar(indice, { reason: { ...requisito.reason, [idioma]: evento.target.value } })
              }
              // `lng` força o idioma da ABA, e não o de quem está logado: este
              // campo escreve o texto em português ou em inglês conforme o
              // seletor acima, e um exemplo na língua errada faz a pessoa
              // acreditar que está no idioma que não está.
              placeholder={t("skills.publish.requirementReasonPlaceholder", { lng: idioma })}
            />
          </div>
        </div>
      ))}

      <Button
        variant="outline"
        size="sm"
        onClick={() => onChange([...requisitos, { uid: novaLinha(), id: "", anyOf: [], reason: {} }])}
      >
        <Plus className="mr-1 h-4 w-4" />
        {t("skills.publish.addRequirement")}
      </Button>
    </div>
  );
};

/** O que a instalação pergunta, e a resposta fica gravada na instalação. */
export const ParametersDeclarationStep = ({
  parametros,
  onChange,
  idioma,
}: {
  parametros: ParametroEmEdicao[];
  onChange: (parametros: ParametroEmEdicao[]) => void;
  idioma: string;
}) => {
  const { t } = useTranslation();

  const trocar = (indice: number, mudanca: Partial<ParametroEmEdicao>) =>
    onChange(parametros.map((item, i) => (i === indice ? { ...item, ...mudanca } : item)));

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{t("skills.publish.parametersHint")}</p>

      {parametros.length === 0 && (
        <p className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
          {t("skills.publish.parametersEmpty")}
        </p>
      )}

      {parametros.map((parametro, indice) => (
        // Pela identidade da LINHA, como no editor de integrações: linha
        // removível com chave por índice faz o React reaproveitar o estado da
        // linha errada depois de uma remoção.
        <div key={parametro.uid} className="space-y-3 rounded-md border p-4">
          <div className="flex items-start gap-3">
            <div className="flex-1 space-y-1.5">
              <Label>{t("skills.publish.parameterKey")}</Label>
              <Input
                value={parametro.key}
                onChange={(evento) => trocar(indice, { key: evento.target.value })}
                placeholder="publico"
              />
            </div>

            <div className="flex-1 space-y-1.5">
              <Label>{t("skills.publish.parameterLabel")}</Label>
              <Input
                value={parametro.label[idioma] ?? ""}
                onChange={(evento) =>
                  trocar(indice, { label: { ...parametro.label, [idioma]: evento.target.value } })
                }
                placeholder={t("skills.publish.parameterLabelPlaceholder", { lng: idioma })}
              />
            </div>

            <Button
              variant="ghost"
              size="icon"
              className="mt-7 text-muted-foreground"
              onClick={() => onChange(parametros.filter((_, i) => i !== indice))}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>

          <div className="flex items-end gap-3">
            <div className="w-44 space-y-1.5">
              <Label>{t("skills.publish.parameterType")}</Label>
              <Select
                value={parametro.type}
                onValueChange={(valor) =>
                  trocar(indice, { type: valor as ParametroEmEdicao["type"] })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="enum">{t("skills.publish.typeEnum")}</SelectItem>
                  <SelectItem value="text">{t("skills.publish.typeText")}</SelectItem>
                  <SelectItem value="number">{t("skills.publish.typeNumber")}</SelectItem>
                  <SelectItem value="boolean">{t("skills.publish.typeBoolean")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1 space-y-1.5">
              <Label>{t("skills.publish.parameterDefault")}</Label>
              <Input
                value={parametro.default}
                onChange={(evento) => trocar(indice, { default: evento.target.value })}
              />
            </div>

            <div className="flex items-center gap-2 pb-2">
              <Switch
                checked={parametro.required}
                onCheckedChange={(marcado) => trocar(indice, { required: marcado })}
              />
              <Label className="text-sm font-normal">{t("skills.publish.parameterRequired")}</Label>
            </div>
          </div>

          {parametro.type === "enum" && (
            <div className="space-y-1.5">
              <Label>{t("skills.publish.parameterOptions")}</Label>
              <Input
                value={parametro.options}
                onChange={(evento) => trocar(indice, { options: evento.target.value })}
                placeholder="Cliente, Interno"
              />
              <div className="flex flex-wrap gap-1 pt-1">
                {parametro.options
                  .split(",")
                  .map((opcao) => opcao.trim())
                  .filter(Boolean)
                  .map((opcao) => (
                    <Badge key={opcao} variant="secondary" className="text-[11px] font-normal">
                      {opcao}
                    </Badge>
                  ))}
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            <Label>{t("skills.publish.parameterHelp")}</Label>
            <Input
              value={parametro.help[idioma] ?? ""}
              onChange={(evento) =>
                trocar(indice, { help: { ...parametro.help, [idioma]: evento.target.value } })
              }
              placeholder={t("skills.publish.parameterHelpPlaceholder", { lng: idioma })}
            />
          </div>

          {/* A recomendação não é estética: o valor respondido aqui entra no que
              o agente lê, e texto livre é a borda por onde passa "ignore as
              instruções anteriores". Lista fechada não tem essa borda. */}
          {parametro.type === "text" && (
            <p className="text-xs text-muted-foreground">{t("skills.publish.preferEnum")}</p>
          )}
        </div>
      ))}

      <Button
        variant="outline"
        size="sm"
        onClick={() =>
          onChange([
            ...parametros,
            {
              uid: novaLinha(),
              key: "",
              type: "enum",
              options: "",
              required: false,
              default: "",
              label: {},
              help: {},
            },
          ])
        }
      >
        <Plus className="mr-1 h-4 w-4" />
        {t("skills.publish.addParameter")}
      </Button>
    </div>
  );
};

/**
 * As mesmas declarações, só que para LER (AB#85634).
 *
 * A administração abre uma versão JÁ PUBLICADA, e versão publicada é imutável:
 * mudar o que ela exige ou pergunta é publicar outra. Por isso aqui não há
 * campo — o que existe é a resposta a "o que esta versão exige" e "o que ela
 * pergunta", que antes não tinha onde ser lida fora do assistente de instalação.
 */
export const DeclarationsSummary = ({
  requisitos,
  parametros,
  vazio,
}: {
  requisitos?: RequisitoEmEdicao[];
  parametros?: ParametroEmEdicao[];
  vazio: string;
}) => {
  const { t, i18n } = useTranslation();

  const nada = (requisitos?.length ?? 0) === 0 && (parametros?.length ?? 0) === 0;

  if (nada) {
    return (
      <p className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
        {vazio}
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {requisitos?.map((requisito) => (
        <div key={requisito.uid} className="space-y-2 rounded-md border p-4">
          <p className="text-sm font-medium">{requisito.id}</p>

          <div className="flex flex-wrap gap-1">
            {requisito.anyOf.map((forma) => (
              <Badge key={forma} variant="secondary" className="text-[11px] font-normal">
                {forma.startsWith(CLASSIFICACAO)
                  ? t("skills.publish.anyOfFamily", { family: forma.slice(CLASSIFICACAO.length) })
                  : forma}
              </Badge>
            ))}
          </div>

          {textoDaSkill(null, requisito.reason, i18n.language) && (
            <p className="text-xs text-muted-foreground">
              {textoDaSkill(null, requisito.reason, i18n.language)}
            </p>
          )}
        </div>
      ))}

      {parametros?.map((parametro) => (
        <div key={parametro.uid} className="space-y-2 rounded-md border p-4">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-medium">
              {textoDaSkill(null, parametro.label, i18n.language) || parametro.key}
            </p>
            <Badge variant="outline" className="text-[11px] font-normal">
              {t(`skills.publish.type${parametro.type === "enum" ? "Enum" : parametro.type === "number" ? "Number" : parametro.type === "boolean" ? "Boolean" : "Text"}`)}
            </Badge>
            {parametro.required && (
              <Badge variant="outline" className="text-[11px] font-normal">
                {t("skills.publish.parameterRequired")}
              </Badge>
            )}
          </div>

          <p className="font-mono text-xs text-muted-foreground">{parametro.key}</p>

          {parametro.options && (
            <div className="flex flex-wrap gap-1">
              {parametro.options
                .split(",")
                .map((opcao) => opcao.trim())
                .filter(Boolean)
                .map((opcao) => (
                  <Badge
                    key={opcao}
                    variant={opcao === parametro.default ? "default" : "secondary"}
                    className="text-[11px] font-normal"
                  >
                    {opcao}
                  </Badge>
                ))}
            </div>
          )}

          {textoDaSkill(null, parametro.help, i18n.language) && (
            <p className="text-xs text-muted-foreground">
              {textoDaSkill(null, parametro.help, i18n.language)}
            </p>
          )}
        </div>
      ))}
    </div>
  );
};
